import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';
import type { AlertDTO } from '@lightbm/shared';
import { useFormatters } from '@/app/app-context';
import { useAuth } from '@/features/auth/auth-context';
import { useErrorText } from '@/i18n/messages';
import { cn } from '@/lib/cn';
import { useAlertsSummary, useMarkAllAlertsRead } from './api';
import { AlertIcon, useAlertText, useAlertWhere } from './parts';

/** Number of new alerts for the bell, the sidebar and the tab bar (0 without alerts:view). */
export function useUnreadAlerts(): number {
  const { can } = useAuth();
  return useAlertsSummary(can('alerts:view')).data?.unread ?? 0;
}

/** Red count of new alerts ("7", "99+"). */
export function UnreadBadge({ count, className }: { count: number; className?: string }) {
  const { t } = useTranslation('alerts');
  if (count === 0) return null;
  return (
    <span
      className={cn(
        'inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-danger px-1.5 text-caption font-semibold text-white tabular',
        className,
      )}
      aria-label={t('bell.unread', { count })}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function BellItem({ alert, onOpen }: { alert: AlertDTO; onOpen: () => void }) {
  const format = useFormatters();
  const { title } = useAlertText()(alert);
  const where = useAlertWhere()(alert);
  return (
    <li>
      <Link
        to="/alerts"
        onClick={onOpen}
        className="flex gap-3 rounded-control px-2 py-2.5 transition-colors hover:bg-ground"
      >
        <AlertIcon type={alert.type} className="size-9 [&>svg]:size-[18px]" />
        <span className="min-w-0">
          <span className="block font-medium text-ink">{title}</span>
          <span className="block text-sm text-ink-2">
            {format.moment(alert.raisedAt)}
            {where && ` · ${where}`}
          </span>
        </span>
      </Link>
    </li>
  );
}

/** ALR-02: the bell of the top bar, with the new alerts and a way to the notification center. */
export function AlertsBell({ className }: { className?: string }) {
  const { t } = useTranslation('alerts');
  const { can } = useAuth();
  const errorText = useErrorText();
  const summary = useAlertsSummary(can('alerts:view'));
  const markAll = useMarkAllAlertsRead();
  if (!can('alerts:view')) return null;
  const unread = summary.data?.unread ?? 0;
  const latest = summary.data?.latest ?? [];

  return (
    <Popover className="relative">
      <PopoverButton
        className={cn('relative', className)}
        aria-label={
          unread > 0
            ? `${t('bell.title')} · ${t('bell.unread', { count: unread })}`
            : t('bell.title')
        }
      >
        <Bell className="size-5" strokeWidth={1.75} />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-surface bg-danger px-1 text-[11px] font-semibold text-white tabular"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </PopoverButton>
      <PopoverPanel
        anchor="bottom end"
        transition
        className="z-40 w-[22rem] max-w-[calc(100vw-2rem)] rounded-control border border-line bg-surface shadow-pop [--anchor-gap:8px] transition data-closed:opacity-0"
      >
        {({ close }) => (
          <>
            <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
              <p className="font-semibold">
                {unread > 0 ? t('bell.unread', { count: unread }) : t('bell.title')}
              </p>
              {unread > 0 && (
                <button
                  type="button"
                  disabled={markAll.isPending}
                  onClick={() =>
                    markAll.mutate(undefined, { onError: (error) => toast.error(errorText(error)) })
                  }
                  className="text-sm font-semibold text-primary hover:underline disabled:opacity-60"
                >
                  {t('markAllReadShort')}
                </button>
              )}
            </div>
            {latest.length > 0 ? (
              <ul className="max-h-96 overflow-y-auto p-2">
                {latest.map((alert) => (
                  <BellItem key={alert.id} alert={alert} onOpen={() => close()} />
                ))}
              </ul>
            ) : (
              <div className="px-4 py-6 text-center">
                <p className="font-semibold">{t('bell.empty')}</p>
                <p className="mt-1 text-sm text-ink-2">{t('bell.emptyText')}</p>
              </div>
            )}
            <Link
              to="/alerts"
              onClick={() => close()}
              className="block border-t border-line px-4 py-3 text-center font-semibold text-primary hover:bg-ground"
            >
              {t('bell.seeAll')}
            </Link>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
}
